import { useMemo } from "react";

import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import { Logo } from "@punto/ui/components/logo";

import { useBusinessAbilities } from "@/modules/abilities";
import { useInboxStats } from "@/modules/inbox";
import { useCanSendEmail } from "@/modules/messaging/hooks/use-can-send-email";
import { routePaths } from "@/router/route-paths";
import { useBaseRoute } from "@/routes/use-base-route";

interface NavItem {
  label: React.ReactNode;
  href: string;
  badge?: number;
}

const useNav = () => {
  const { t } = useTranslation();

  const baseRoute = useBaseRoute();

  const homeLink = {
    label: (
      <div className="p-2">
        <Logo />
      </div>
    ),
    href: baseRoute,
  };

  const {
    canManageProcedures,
    canManageEnergySavingCertificates,
    canManageSignatures,
  } = useBusinessAbilities();

  const canSendEmail = useCanSendEmail();
  const { pending: pendingConversations } = useInboxStats({
    // Both hooks are called ALWAYS and the data one is switched off with
    // `enabled`: without it the request would fire for people who will never
    // see the entry.
    enabled: canManageProcedures && canSendEmail,
  });

  const menuItems = useMemo(() => {
    const items: NavItem[] = [];

    if (canManageProcedures) {
      items.push({
        label: t("Header.menu_procedures"),
        href: routePaths.procedureViewsIndex,
      });

      if (canSendEmail) {
        items.push({
          label: t("Header.menu_messages"),
          href: routePaths.messages,
          badge: pendingConversations || undefined,
        });
      }

      items.push({
        label: t("Header.menu_reports"),
        href: routePaths.reports,
      });

      items.push({
        label: t("Header.menu_customers"),
        href: routePaths.customers,
      });
    }

    if (canManageSignatures) {
      items.push({
        label: t("Header.menu_signatures"),
        href: routePaths.signatures,
      });
    }

    // Last on purpose (P-2863): for an agency that lives in both worlds the AUTO
    // entries are the everyday ones, so "Actuaciones" closes the menu instead of
    // splitting them. For an ESC-only agency it is the only entry, so the
    // position is moot.
    if (canManageEnergySavingCertificates) {
      items.push({
        label: t("Header.menu_energy_saving_certificates"),
        href: routePaths.energySavingCertificates,
      });
    }

    return items;
  }, [
    t,
    canManageProcedures,
    canManageEnergySavingCertificates,
    canManageSignatures,
    canSendEmail,
    pendingConversations,
  ]);

  return { homeLink, menuItems };
};

export { useNav, type NavItem };
